import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import _ from 'lodash'

import backendUrl from '../../apiConfig'

export default function SignupPage() {
    const navigate = useNavigate()
    const [step, setStep] = useState(1)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [username, setUsername] = useState('')
    const [error, setError] = useState('')
    const [usernameError, setUsernameError] = useState('')
    const [suggestions, setSuggestions] = useState([])

    const emailRef = useRef(null)
    const passwordRef = useRef(null)
    const usernameRef = useRef(null)

    useEffect(() => {
        const token = localStorage.getItem("jwtToken")
        if (token) {
            navigate("/admin", { replace: true })
        }
    }, [navigate])

    useEffect(() => {
        if (step === 1 && emailRef.current) {
            emailRef.current.focus()
        } else if (step === 2 && passwordRef.current) {
            passwordRef.current.focus()
        } else if (step === 3 && usernameRef.current) {
            usernameRef.current.focus()
        }
    }, [step])

    const validateEmail = useCallback(
        _.debounce(async (emailToValidate) => {
            if (!emailToValidate) {
                setError('')
                return
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(emailToValidate)) {
                setError('Please enter a valid email address')
                return
            }

            try {
                await axios.post(`${backendUrl}/auth/email`, { email: emailToValidate })
                setError('')
            } catch (err) {
                if (err.response?.status === 400) {
                    setError('Email is already in use')
                } else {
                    console.error(err)
                    setError('An error occurred while checking the email.')
                }
            }
        }, 300),
        []
    )

    const handleEmailChange = (e) => {
        const newEmail = e.target.value
        setEmail(newEmail)
        validateEmail(newEmail)
    }

    const validateUsername = useCallback(
        _.debounce(async (usernameToValidate) => {
            if (!usernameToValidate) {
                setUsernameError('')
                return
            }

            try {
                await axios.post(`${backendUrl}/auth/username`, { username: usernameToValidate })
            } catch (err) {
                if (err.response?.status === 400) {
                    setUsernameError('Username is already taken')
                } else {
                    console.error(err)
                    setUsernameError('An error occurred while checking the username.')
                }
            }
        }, 300),
        []
    )

    const handleUsernameChange = (e) => {
        const newUsername = e.target.value
        setUsername(newUsername)
        validateUsername(newUsername)
    }

    const handleEmailSubmit = async (e) => {
        e.preventDefault()
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!email || !emailRegex.test(email)) {
            setError('Please enter a valid email address')
            return
        }

        if (!error) {
            setStep(2)
        }
    }

    const handlePasswordSubmit = async (e) => {
        e.preventDefault()
        if (password.length < 6) {
            setError('Password must be at least 6 characters long')
            return
        }

        try {
            const response = await axios.post(
                `${backendUrl}/auth/register`,
                { email, password }
            )

            if (response.status === 201) {
                const token = response.data.token
                localStorage.setItem('jwtToken', token)

                setStep(3)
                const baseUsername = email.split('@')[0]
                setSuggestions([
                    `${baseUsername}123`,
                    `${baseUsername}456`,
                    `${baseUsername}789`
                ])
            } else {
                setError(response.data.error || 'Registration failed')
            }
        } catch (err) {
            console.error(err)
            setError('Email is already used')
            setStep(1)
        }
    }

    const handleUsernameSubmit = async (e) => {
        e.preventDefault()
        if (username) {
            try {
                const response = await axios.post(`${backendUrl}/auth/storeUsername`, { email, username })

                if (response.status === 200) {
                    // alert('Registration successful with username: ' + username)
                    navigate("/theme")
                    // navigate("/admin")
                } else {
                    setUsernameError('Failed to store username')
                }
            } catch (err) {
                console.error(err)
                setUsernameError('An error occurred while saving the username.')
            }
        }
    }

    return (
        <div className="min-h-screen flex bg-white">
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-sm">
                    <div className="mb-8">
                        {/* <svg
                            className="h-8 w-auto"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
                            <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
                            <path d="M12 3v6" />
                        </svg> */}
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight text-black mb-2">Join SpellTree</h1>
                    <p className="text-slate-600 mb-8">Sign up for free!</p>
                    {step === 1 && (
                        <form onSubmit={handleEmailSubmit} className="space-y-4">
                            <input
                                type="email"
                                value={email}
                                onChange={handleEmailChange}
                                placeholder="Email"
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-transparent focus:ring-2 focus:ring-black focus:border-transparent"
                                required
                                ref={emailRef}
                            />
                            {error && <p className="text-red-500 text-sm">{error}</p>}
                            <button type="submit" className="w-full bg-black hover:bg-slate-800 text-white py-2 rounded-lg transition-colors">
                                Continue
                            </button>
                        </form>
                    )}
                    {step === 2 && (
                        <form onSubmit={handlePasswordSubmit} className="space-y-4">
                            <button onClick={() => setStep(1)} className="text-black mb-4">← Back</button>
                            <h2 className="text-xl font-bold mb-2">Create a Password</h2>
                            <p className="text-slate-600 mb-4">Secure your account</p>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                                required
                                minLength={6}
                                ref={passwordRef}
                            />
                            {error && <p className="text-red-500 text-sm">{error}</p>}
                            <button type="submit" className="w-full bg-black hover:bg-slate-800 text-white py-2 rounded-lg transition-colors">
                                Continue
                            </button>
                        </form>
                    )}
                    {step === 3 && (
                        <form onSubmit={handleUsernameSubmit} className="space-y-4">
                            <button onClick={() => setStep(2)} className="text-black mb-4">← Back</button>
                            <h2 className="text-xl font-bold mb-2">Choose a Username</h2>
                            <p className="text-slate-600 mb-4">Pick a unique username for your profile</p>
                            <input
                                type="text"
                                value={username}
                                onChange={handleUsernameChange}
                                placeholder="Username"
                                className="w-full bg-white px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                                required
                                ref={usernameRef}
                            />
                            {usernameError && <p className="text-red-500 text-sm">{usernameError}</p>}
                            <div className="text-sm text-slate-500">Suggestions: {suggestions.join(', ')}</div>
                            <button type="submit" className="w-full bg-black hover:bg-slate-800 text-white py-2 rounded-lg transition-colors">
                                Complete Registration
                            </button>
                        </form>
                    )}
                    <div className="mt-8 text-center">
                        <div className="text-sm text-black mb-4">OR</div>
                        <div className="space-y-3">
                            <button className="w-full flex items-center justify-center gap-2 border text-black border-slate-200 rounded-lg px-3 py-2 text-sm font-medium hover:bg-slate-50 transition-colors">
                                <svg className="h-5 w-5" viewBox="0 0 24 24">
                                    <path
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                        fill="#4285F4"
                                    />
                                    <path
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        fill="#34A853"
                                    />
                                    <path
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                        fill="#FBBC05"
                                    />
                                    <path
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        fill="#EA4335"
                                    />
                                </svg>
                                Sign up with Google
                            </button>
                            <button className="w-full flex items-center justify-center gap-2 text-black border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium hover:bg-slate-50 transition-colors">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z" />
                                </svg>
                                Sign up with Apple
                            </button>
                        </div>
                    </div>
                    <div className="mt-8 text-center text-sm text-slate-600">
                        Already have an account?{" "}
                        <a href="/login" className="text-black font-medium hover:underline">
                            Login
                        </a>
                    </div>
                    <p className="mt-8 text-xs text-center text-slate-500">
                        By clicking Continue, you agree to our{" "}
                        <a href="/terms" className="underline hover:text-black">
                            Terms
                        </a>{" "}
                        and{" "}
                        <a href="/privacy" className="underline hover:text-black">
                            Privacy Policy
                        </a>
                        .
                    </p>
                </div>
            </div>
            <div className="hidden lg:flex flex-1 items-center justify-center text-black bg-amber-400 p-12">
                <div className="max-w-lg">
                    <h2 className="text-4xl font-bold mb-6">Create your digital identity</h2>
                    <p className="text-lg mb-8">
                        Join millions of creators, artists, and businesses who use SpellTree to share everything they create, curate and sell online.
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-black/10 rounded-lg p-4">
                            <div className="font-medium mb-2">Share</div>
                            <p className="text-sm">Connect your audience to all of your content</p>
                        </div>
                        <div className="bg-black/10 rounded-lg p-4">
                            <div className="font-medium mb-2">Share</div>
                            <p className="text-sm">share your work through one link</p>
                        </div>
                        <div className="bg-black/10 rounded-lg p-4">
                            <div className="font-medium mb-2">Grow</div>
                            <p className="text-sm">Build your brand and grow your audience</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}